import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { format } from 'date-fns';
import { RefreshCw, CalendarDays, Users, Clock, MapPin, Camera, AlertTriangle, CheckCircle, XCircle, Eye, Download, Search, Pencil, UserCheck, CalendarRange, HeartHandshake } from 'lucide-react';
import { toast } from 'sonner';
import { isAdmin, isHR, getUser } from '@/lib/auth';
import api from '@/lib/api';

const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];

function canViewAll() {
  const user = getUser();
  return user?.role === 'admin' || user?.role === 'super_admin' || user?.role === 'hr';
}

export default function AttendancePage() {
  const now = new Date();
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());
  const [records, setRecords] = useState([]);
  const [summary, setSummary] = useState([]);
  const [liveData, setLiveData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [detailRecord, setDetailRecord] = useState(null);
  const [showDetail, setShowDetail] = useState(false);
  const [search, setSearch] = useState('');
  const [deptFilter, setDeptFilter] = useState('All');
  const [employees, setEmployees] = useState([]);
  const [empFilter, setEmpFilter] = useState('All');
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({
    mode: 'single', // 'single' | 'bulk'
    employee_id: '', date: new Date().toISOString().slice(0, 10),
    from_date: new Date().toISOString().slice(0, 10),
    to_date: new Date().toISOString().slice(0, 10),
    status: 'present', clock_in_time: '', clock_out_time: '',
    remarks: '', reason_for_edit: '',
    skip_sundays: true, skip_existing: true,
  });
  const [editPrefilled, setEditPrefilled] = useState(false);
  const [editingRecordId, setEditingRecordId] = useState(null);
  const [editSaving, setEditSaving] = useState(false);
  // Absence-compensation dialog
  const [compensateOpen, setCompensateOpen] = useState(false);
  const [compensateRecord, setCompensateRecord] = useState(null);
  const [compensateForm, setCompensateForm] = useState({ leave_type: 'AUTO', reason: '' });
  const [compensateBalances, setCompensateBalances] = useState({ CL: 0, EL: 0, SL: 0 });
  const [compensateSaving, setCompensateSaving] = useState(false);

  const openCompensateDialog = async (record) => {
    setCompensateRecord(record);
    setCompensateForm({ leave_type: 'AUTO', reason: '' });
    setCompensateOpen(true);
    try {
      const { data } = await api.get('/api/leave/all-balances');
      const row = (data || []).find(r => r.id === record.employee_id);
      const b = row?.balances || {};
      setCompensateBalances({
        CL: b.casual_leave?.balance || 0,
        EL: b.earned_leave?.balance || 0,
        SL: b.sick_leave?.balance || 0,
      });
    } catch { setCompensateBalances({ CL: 0, EL: 0, SL: 0 }); }
  };

  const handleCompensateSave = async () => {
    if (!compensateRecord || !compensateForm.reason.trim()) { toast.error('Reason is required'); return; }
    const isHalf = compensateRecord.status === 'half_day' || (compensateRecord.status !== 'absent' && compensateRecord.half_day_deducted);
    const needed = isHalf ? 0.5 : 1;
    const totalAvail = (compensateBalances.CL || 0) + (compensateBalances.EL || 0) + (compensateBalances.SL || 0);
    const bucketCheck = compensateForm.leave_type === 'AUTO' ? totalAvail : (compensateBalances[compensateForm.leave_type] || 0);
    if (bucketCheck < needed) {
      toast.error(compensateForm.leave_type === 'AUTO'
        ? `Insufficient balance across CL + EL + SL (${totalAvail} available, need ${needed})`
        : `Insufficient ${compensateForm.leave_type} balance (need ${needed})`);
      return;
    }
    setCompensateSaving(true);
    try {
      await api.post('/api/attendance/compensate-absence', {
        employee_id: compensateRecord.employee_id,
        dates: [compensateRecord.date],
        leave_type: compensateForm.leave_type,
        reason: compensateForm.reason.trim(),
        half_day: isHalf,
      });
      toast.success(`${isHalf ? 'Half-day' : 'Absence'} on ${compensateRecord.date} compensated (${compensateForm.leave_type === 'AUTO' ? 'Auto cascade' : compensateForm.leave_type})`);
      setCompensateOpen(false);
      loadMonthly();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to compensate'); }
    setCompensateSaving(false);
  };

  const fullAccess = canViewAll();

  const loadMonthly = async () => {
    setLoading(true);
    try {
      const params = { month, year };
      if (fullAccess && empFilter !== 'All') params.employee_id = empFilter;
      if (fullAccess && deptFilter !== 'All') params.department = deptFilter;
      if (fullAccess) params.include_absent = true;
      const promises = [api.get('/api/attendance', { params })];
      if (fullAccess) promises.push(api.get('/api/attendance/summary', { params: { month, year } }));
      const results = await Promise.all(promises);
      setRecords(results[0].data);
      if (fullAccess) setSummary(results[1].data);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const loadLive = async () => {
    if (!fullAccess) return;
    try {
      const { data } = await api.get('/api/attendance/today-live');
      setLiveData(data);
    } catch (e) { console.error(e); }
  };

  const loadEmployees = async () => {
    if (!fullAccess) return;
    try {
      const { data } = await api.get('/api/employees?limit=200');
      setEmployees(data.employees || []);
    } catch (e) { console.error(e); }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadMonthly(); }, [month, year, empFilter, deptFilter]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (fullAccess) { loadLive(); loadEmployees(); } }, []);

  const handleBulkMark = async () => {
    const today = new Date().toISOString().split('T')[0];
    try {
      const { data } = await api.post(`/api/attendance/bulk-mark?date=${today}`);
      toast.success(data.message);
      loadMonthly();
      loadLive();
    } catch (e) { toast.error('Failed'); }
  };

  const viewDetail = async (recordId) => {
    try {
      const { data } = await api.get(`/api/attendance/detail/${recordId}`);
      setDetailRecord(data);
      setShowDetail(true);
    } catch (e) { toast.error('Failed to load details'); }
  };

  const openEditDialog = async (record = null) => {
    if (record) {
      // Edit existing record - always single mode
      setEditingRecordId(record.is_synthetic ? null : record.id);
      setEditForm({
        mode: 'single',
        employee_id: record.employee_id,
        date: record.date,
        from_date: record.date, to_date: record.date,
        status: record.is_synthetic ? 'present' : (record.status || 'present'),
        clock_in_time: record.clock_in_time || '',
        clock_out_time: record.clock_out_time || '',
        remarks: record.notes || '',
        reason_for_edit: '',
        skip_sundays: true, skip_existing: true,
      });
      setEditPrefilled(!record.is_synthetic);
    } else {
      // Mark fresh attendance — let user pick employee & date
      setEditingRecordId(null);
      const today = new Date().toISOString().slice(0, 10);
      setEditForm({
        mode: 'single',
        employee_id: '', date: today,
        from_date: today, to_date: today,
        status: 'present', clock_in_time: '', clock_out_time: '',
        remarks: '', reason_for_edit: '',
        skip_sundays: true, skip_existing: true,
      });
      setEditPrefilled(false);
    }
    setEditOpen(true);
  };

  const lookupAttendance = async () => {
    if (!editForm.employee_id || !editForm.date) return;
    try {
      const { data } = await api.get('/api/attendance/record', { params: { employee_id: editForm.employee_id, date: editForm.date } });
      setEditingRecordId(data.id);
      setEditForm(f => ({
        ...f,
        status: data.status || 'present',
        clock_in_time: data.clock_in_time || '',
        clock_out_time: data.clock_out_time || '',
        remarks: data.notes || '',
      }));
      setEditPrefilled(true);
      toast.info('Existing record loaded');
    } catch (e) {
      if (e.response?.status === 404) {
        setEditingRecordId(null);
        setEditPrefilled(false);
        toast.info('No record yet — will create a fresh entry on save');
      }
    }
  };

  const handleSaveEdit = async () => {
    if (!editForm.employee_id) { toast.error('Pick an employee'); return; }
    if (!editForm.reason_for_edit.trim()) { toast.error('Reason for edit is required'); return; }

    setEditSaving(true);
    try {
      if (editForm.mode === 'bulk') {
        if (!editForm.from_date || !editForm.to_date) { toast.error('Pick a date range'); setEditSaving(false); return; }
        if (editForm.from_date > editForm.to_date) { toast.error('From-Date must be on or before To-Date'); setEditSaving(false); return; }
        const { data } = await api.post('/api/attendance/bulk-edit-range', {
          employee_id: editForm.employee_id,
          from_date: editForm.from_date,
          to_date: editForm.to_date,
          status: editForm.status,
          clock_in_time: editForm.clock_in_time,
          clock_out_time: editForm.clock_out_time,
          remarks: editForm.remarks,
          reason_for_edit: editForm.reason_for_edit,
          skip_sundays: editForm.skip_sundays,
          skip_existing: editForm.skip_existing,
        });
        toast.success(data.message);
      } else {
        if (!editForm.date) { toast.error('Pick a date'); setEditSaving(false); return; }
        const { data } = await api.put('/api/attendance/record', {
          employee_id: editForm.employee_id,
          date: editForm.date,
          status: editForm.status,
          clock_in_time: editForm.clock_in_time,
          clock_out_time: editForm.clock_out_time,
          remarks: editForm.remarks,
          reason_for_edit: editForm.reason_for_edit,
        });
        toast.success(data.action === 'attendance_create' ? 'Attendance marked' : 'Attendance updated');
      }
      setEditOpen(false);
      await loadMonthly();
      if (fullAccess) await loadLive();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    }
    setEditSaving(false);
  };

  const handleExport = async (format) => {
    try {
      const params = { month, year, format };
      if (empFilter !== 'All') params.employee_id = empFilter;
      if (deptFilter !== 'All') params.department = deptFilter;
      const r = await api.get('/api/attendance/export', { params, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `attendance_${MONTHS[month - 1]}_${year}.${format}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`${format.toUpperCase()} exported`);
    } catch (e) { toast.error('Export failed'); }
  };

  // Get unique departments from records for filter
  const departments = useMemo(() => {
    const depts = new Set(employees.map(e => e.department).filter(Boolean));
    return [...depts].sort();
  }, [employees]);

  // Filter records by search text (client side)
  const filteredRecords = useMemo(() => {
    if (!search) return records;
    const s = search.toLowerCase();
    return records.filter(r => r.employee_name?.toLowerCase().includes(s) || r.employee_code?.toLowerCase().includes(s));
  }, [records, search]);

  const statusBadge = (s, isLate, halfDay, leaveType) => {
    if (s === 'late' || isLate) return (
      <div className="flex flex-col gap-1">
        <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100 text-xs w-fit">Late</Badge>
        {halfDay && <Badge className="bg-red-100 text-red-600 hover:bg-red-100 text-[10px] w-fit">Half-day deducted</Badge>}
      </div>
    );
    if (s === 'on_leave' && (leaveType || '').toUpperCase() === 'MLP') return <Badge className="bg-pink-100 text-pink-700 hover:bg-pink-100 text-xs" data-testid="status-badge-mlp">MLP</Badge>;
    if (s === 'on_leave' && (leaveType || '').toUpperCase() === 'COMP_OFF') return <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100 text-xs" data-testid="status-badge-compoff">Comp-Off</Badge>;
    if (s === 'present') return <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100 text-xs">Present</Badge>;
    if (s === 'absent') return <Badge className="bg-red-100 text-red-700 hover:bg-red-100 text-xs">Absent</Badge>;
    if (s === 'half_day') return <Badge className="bg-orange-100 text-orange-700 hover:bg-orange-100 text-xs">Half Day</Badge>;
    if (s === 'on_leave') return <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100 text-xs">On Leave</Badge>;
    if (s === 'holiday') return <Badge className="bg-violet-100 text-violet-700 hover:bg-violet-100 text-xs">Holiday</Badge>;
    if (s === 'wfh') return <Badge className="bg-teal-100 text-teal-700 hover:bg-teal-100 text-xs">WFH</Badge>;
    if (s === 'weekly_off') return <Badge className="bg-slate-100 text-slate-500 hover:bg-slate-100 text-xs">Weekly Off</Badge>;
    return <Badge className="bg-slate-100 text-slate-600 hover:bg-slate-100 text-xs">{s}</Badge>;
  };

  return (
    <div className="space-y-6" data-testid="attendance-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <Badge className="bg-[#0D2850] text-white text-[10px] mb-2 hover:bg-[#0D2850]">// ATTENDANCE</Badge>
          <h1 className="text-2xl font-semibold text-[#0B1B33] tracking-tight">
            {fullAccess ? 'Attendance Management' : 'My Attendance'}
          </h1>
          <p className="text-sm text-slate-500">
            {fullAccess ? 'Selfie + GPS verified clock-in/out' : 'View your attendance records'}
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {fullAccess && (
            <>
              <Button variant="outline" size="sm" className="gap-1" onClick={() => handleExport('csv')} data-testid="export-attendance-csv">
                <Download className="w-3 h-3" /> CSV
              </Button>
              <Button variant="outline" size="sm" className="gap-1" onClick={() => handleExport('pdf')} data-testid="export-attendance-pdf">
                <Download className="w-3 h-3" /> PDF
              </Button>
              <Button size="sm" variant="outline" className="gap-1 border-[#0D2850]/30 text-[#0D2850] hover:bg-[#0D2850]/5" onClick={() => openEditDialog(null)} data-testid="mark-edit-attendance-btn">
                <UserCheck className="w-3.5 h-3.5" /> Mark / Edit Attendance
              </Button>
              <Button size="sm" className="bg-[#0D2850] hover:bg-[#0B2246]" onClick={handleBulkMark} data-testid="bulk-mark-attendance">
                Mark All Present Today
              </Button>
            </>
          )}
        </div>
      </div>

      {fullAccess ? (
        <Tabs defaultValue="live" className="space-y-4">
          <TabsList className="bg-slate-100">
            <TabsTrigger value="live" className="data-[state=active]:bg-[#0D2850] data-[state=active]:text-white">Live Today</TabsTrigger>
            <TabsTrigger value="monthly" className="data-[state=active]:bg-[#0D2850] data-[state=active]:text-white">Monthly View</TabsTrigger>
          </TabsList>

          {/* Live Today Tab */}
          <TabsContent value="live" className="space-y-4">
            {liveData && (
              <>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                  {[
                    { label: 'Total', value: liveData.total_employees, icon: Users, color: 'text-[#0D2850]', bg: 'bg-blue-50' },
                    { label: 'Clocked In', value: liveData.clocked_in, icon: CheckCircle, color: 'text-emerald-600', bg: 'bg-emerald-50' },
                    { label: 'Not In', value: liveData.not_clocked_in, icon: XCircle, color: 'text-red-500', bg: 'bg-red-50' },
                    { label: 'Late', value: liveData.late, icon: AlertTriangle, color: 'text-amber-600', bg: 'bg-amber-50' },
                    { label: 'Clocked Out', value: liveData.clocked_out, icon: Clock, color: 'text-slate-500', bg: 'bg-slate-50' },
                  ].map((s, i) => (
                    <Card key={i} className="border-slate-200">
                      <CardContent className="p-3">
                        <div className="flex items-center gap-2">
                          <div className={`w-8 h-8 rounded-lg ${s.bg} flex items-center justify-center`}>
                            <s.icon className={`w-4 h-4 ${s.color}`} />
                          </div>
                          <div>
                            <p className="text-xs text-slate-500">{s.label}</p>
                            <p className="text-xl font-semibold font-tabular">{s.value}</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
                <Card className="border-slate-200">
                  <CardHeader className="pb-2 flex flex-row items-center justify-between">
                    <CardTitle className="text-sm font-semibold">Today's Clock-Ins ({liveData.date})</CardTitle>
                    <Button variant="ghost" size="sm" onClick={loadLive}><RefreshCw className="w-3 h-3 mr-1" />Refresh</Button>
                  </CardHeader>
                  <CardContent className="p-0">
                    {liveData.records.length === 0 ? (
                      <div className="p-12 text-center">
                        <CalendarDays className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                        <p className="text-sm text-slate-400">No clock-ins yet today</p>
                      </div>
                    ) : (
                      <div className="overflow-x-auto">
                        <Table>
                          <TableHeader>
                            <TableRow className="bg-slate-50">
                              <TableHead>Employee</TableHead>
                              <TableHead>Status</TableHead>
                              <TableHead>Clock In</TableHead>
                              <TableHead className="hidden sm:table-cell">Clock Out</TableHead>
                              <TableHead className="hidden md:table-cell">GPS</TableHead>
                              <TableHead className="w-[60px] hidden sm:table-cell">Proof</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {liveData.records.map(r => (
                              <TableRow key={r.id} className={r.is_synthetic ? 'bg-red-50/40' : (r.is_late ? 'bg-amber-50/30' : '')}>
                                <TableCell>
                                  <div className="flex items-center gap-2">
                                    <Avatar className="w-8 h-8"><AvatarFallback className={`text-xs ${r.is_synthetic ? 'bg-red-200 text-red-700' : 'bg-[#0D2850] text-white'}`}>{r.employee_name?.split(' ').map(n => n[0]).join('').slice(0, 2)}</AvatarFallback></Avatar>
                                    <div>
                                      <p className="text-sm font-medium">{r.employee_name}</p>
                                      <p className="text-xs text-slate-400">{r.employee_code} - {r.department}</p>
                                    </div>
                                  </div>
                                </TableCell>
                                <TableCell>{statusBadge(r.status, r.is_late, r.half_day_deducted, r.leave_type)}</TableCell>
                                <TableCell className="text-sm font-medium">{r.clock_in_time || (r.is_synthetic ? <span className="text-slate-400 italic">No clock-in</span> : '-')}</TableCell>
                                <TableCell className="text-sm hidden sm:table-cell">{r.clock_out_time || (r.is_synthetic ? <span className="text-slate-400">-</span> : <span className="text-slate-400 italic">Active</span>)}</TableCell>
                                <TableCell className="hidden md:table-cell">
                                  {r.clock_in_gps ? (
                                    <a href={`https://www.google.com/maps?q=${r.clock_in_gps.lat},${r.clock_in_gps.lng}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs text-blue-600 hover:underline"><MapPin className="w-3 h-3" /> Map</a>
                                  ) : <span className="text-xs text-slate-400">-</span>}
                                </TableCell>
                                <TableCell className="hidden sm:table-cell">
                                  <div className="flex items-center gap-0.5">
                                    {!r.is_synthetic && <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => viewDetail(r.id)} data-testid={`view-detail-${r.id}`}><Eye className="w-4 h-4 text-slate-500" /></Button>}
                                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0" title={r.is_synthetic ? 'Mark attendance' : 'Edit attendance'} onClick={() => openEditDialog(r)} data-testid={`edit-attendance-${r.id}`}><Pencil className="w-4 h-4 text-[#0D2850]" /></Button>
                                  </div>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </>
            )}
          </TabsContent>

          {/* Monthly View Tab */}
          <TabsContent value="monthly" className="space-y-4">
            <MonthlyFilters
              month={month} year={year} setMonth={setMonth} setYear={setYear}
              search={search} setSearch={setSearch}
              deptFilter={deptFilter} setDeptFilter={setDeptFilter}
              empFilter={empFilter} setEmpFilter={setEmpFilter}
              departments={departments} employees={employees}
              onRefresh={loadMonthly} fullAccess={fullAccess}
            />
            {summary.length > 0 && (
              <Card className="border-slate-200">
                <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold">Department Summary</CardTitle></CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
                    {summary.map((s, i) => (
                      <div key={i} className="bg-slate-50 rounded-lg p-3">
                        <p className="text-xs font-semibold text-slate-500">{s._id}</p>
                        <div className="flex gap-2 mt-1.5">
                          <span className="text-xs text-emerald-600 font-medium">{s.present}P</span>
                          <span className="text-xs text-red-500 font-medium">{s.absent}A</span>
                          <span className="text-xs text-amber-500 font-medium">{s.late}L</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
            <RecordsTable records={filteredRecords} loading={loading} viewDetail={viewDetail} fullAccess={fullAccess} statusBadge={statusBadge} onEdit={openEditDialog} onCompensate={openCompensateDialog} />
          </TabsContent>
        </Tabs>
      ) : (
        <>
          <MonthlyFilters
            month={month} year={year} setMonth={setMonth} setYear={setYear}
            search={search} setSearch={setSearch}
            onRefresh={loadMonthly} fullAccess={false}
          />
          <RecordsTable records={filteredRecords} loading={loading} viewDetail={viewDetail} fullAccess={false} statusBadge={statusBadge} />
        </>
      )}

      {/* Detail Dialog */}
      <Dialog open={showDetail} onOpenChange={setShowDetail}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Attendance Detail</DialogTitle>
            <DialogDescription>{detailRecord?.employee_name} - {detailRecord?.date}</DialogDescription>
          </DialogHeader>
          {detailRecord && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div><span className="text-slate-500">Employee:</span> <span className="font-medium">{detailRecord.employee_name}</span></div>
                <div><span className="text-slate-500">Code:</span> <span className="font-medium">{detailRecord.employee_code}</span></div>
                <div><span className="text-slate-500">Department:</span> <span className="font-medium">{detailRecord.department}</span></div>
                <div>
                  <span className="text-slate-500">Status:</span>{' '}
                  {detailRecord.is_late ? <Badge className="bg-amber-100 text-amber-700 text-xs">Late</Badge> : <Badge className="bg-emerald-100 text-emerald-700 text-xs">Present</Badge>}
                </div>
              </div>
              <div className="border rounded-lg p-3 space-y-2">
                <p className="text-xs font-semibold text-slate-500 uppercase">Clock In - {detailRecord.clock_in_time}</p>
                <div className="flex gap-3">
                  {detailRecord.clock_in_selfie ? (
                    <img src={`data:image/jpeg;base64,${detailRecord.clock_in_selfie}`} alt="Clock-in selfie" className="w-24 h-18 rounded-md object-cover border" style={{ transform: 'scaleX(-1)' }} data-testid="clock-in-selfie" />
                  ) : (
                    <div className="w-24 h-18 rounded-md bg-slate-100 flex items-center justify-center"><Camera className="w-5 h-5 text-slate-300" /></div>
                  )}
                  <div className="text-xs space-y-1">
                    {detailRecord.clock_in_gps && (
                      <a href={`https://www.google.com/maps?q=${detailRecord.clock_in_gps.lat},${detailRecord.clock_in_gps.lng}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-blue-600 hover:underline">
                        <MapPin className="w-3 h-3" />{detailRecord.clock_in_gps.lat.toFixed(6)}, {detailRecord.clock_in_gps.lng.toFixed(6)}
                      </a>
                    )}
                    {detailRecord.is_late && <p className="text-amber-600 flex items-center gap-1"><AlertTriangle className="w-3 h-3" />Late (instance #{detailRecord.late_count_this_month})</p>}
                    {detailRecord.half_day_deducted && <p className="text-red-600 flex items-center gap-1"><AlertTriangle className="w-3 h-3" />Half-day deduction applied</p>}
                  </div>
                </div>
              </div>
              <div className="border rounded-lg p-3 space-y-2">
                <p className="text-xs font-semibold text-slate-500 uppercase">Clock Out - {detailRecord.clock_out_time || <span className="text-amber-500">Not yet</span>}</p>
                {detailRecord.clock_out_time ? (
                  <div className="flex gap-3">
                    {detailRecord.clock_out_selfie ? (
                      <img src={`data:image/jpeg;base64,${detailRecord.clock_out_selfie}`} alt="Clock-out selfie" className="w-24 h-18 rounded-md object-cover border" style={{ transform: 'scaleX(-1)' }} data-testid="clock-out-selfie" />
                    ) : (
                      <div className="w-24 h-18 rounded-md bg-slate-100 flex items-center justify-center"><Camera className="w-5 h-5 text-slate-300" /></div>
                    )}
                    <div className="text-xs space-y-1">
                      {detailRecord.clock_out_gps && (
                        <a href={`https://www.google.com/maps?q=${detailRecord.clock_out_gps.lat},${detailRecord.clock_out_gps.lng}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-blue-600 hover:underline">
                          <MapPin className="w-3 h-3" />{detailRecord.clock_out_gps.lat.toFixed(6)}, {detailRecord.clock_out_gps.lng.toFixed(6)}
                        </a>
                      )}
                    </div>
                  </div>
                ) : <p className="text-xs text-slate-400 italic">Employee has not clocked out yet</p>}
              </div>
              {detailRecord.notes && <p className="text-xs text-slate-500 bg-slate-50 p-2 rounded">{detailRecord.notes}</p>}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Mark / Edit Attendance Dialog (Admin/HR only) */}
      {/* Compensate Absence Dialog */}
      <Dialog open={compensateOpen} onOpenChange={setCompensateOpen}>
        <DialogContent className="sm:max-w-md" data-testid="compensate-dialog">
          <DialogHeader>
            <DialogTitle>Compensate Absence with Leave</DialogTitle>
          </DialogHeader>
          {compensateRecord && (() => {
            const isHalf = compensateRecord.status === 'half_day' || (compensateRecord.status !== 'absent' && compensateRecord.half_day_deducted);
            const needed = isHalf ? 0.5 : 1;
            return (
            <div className="space-y-3 mt-2">
              <div className={`rounded-md p-2.5 text-xs ${isHalf ? 'bg-amber-50 border border-amber-200' : 'bg-slate-50'}`}>
                <div className="font-medium text-slate-700">{compensateRecord.employee_name} ({compensateRecord.employee_code})</div>
                <div className="text-slate-500 mt-0.5">
                  {isHalf ? <>Half-day on <strong>{compensateRecord.date}</strong> — compensate with <strong>0.5 day</strong> paid leave</> : <>Absent on <strong>{compensateRecord.date}</strong> — compensate with <strong>1 day</strong> paid leave</>}
                </div>
              </div>
              <div>
                <Label className="text-xs">Leave Type</Label>
                <Select value={compensateForm.leave_type} onValueChange={v => setCompensateForm({ ...compensateForm, leave_type: v })}>
                  <SelectTrigger data-testid="compensate-leave-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="AUTO" disabled={(compensateBalances.CL + compensateBalances.EL + compensateBalances.SL) < needed}>
                      Auto (CL → EL → SL) — {(compensateBalances.CL + compensateBalances.EL + compensateBalances.SL)} total available
                    </SelectItem>
                    <SelectItem value="CL" disabled={compensateBalances.CL < needed}>Casual Leave (CL) — {compensateBalances.CL} available</SelectItem>
                    <SelectItem value="EL" disabled={compensateBalances.EL < needed}>Earned Leave (EL) — {compensateBalances.EL} available</SelectItem>
                    <SelectItem value="SL" disabled={compensateBalances.SL < needed}>Sick Leave (SL) — {compensateBalances.SL} available</SelectItem>
                  </SelectContent>
                </Select>
                {compensateForm.leave_type === 'AUTO' ? (
                  <p className="text-[11px] text-slate-500 mt-1">Will consume CL first, then EL, then SL — only days with no balance anywhere become LOP.</p>
                ) : compensateBalances[compensateForm.leave_type] < needed && (
                  <p className="text-[11px] text-red-600 mt-1">⚠️ Insufficient {compensateForm.leave_type} balance (need {needed}) — pick another type or Auto.</p>
                )}
              </div>
              <div>
                <Label className="text-xs">Reason / Justification <span className="text-red-500">*</span></Label>
                <Textarea
                  value={compensateForm.reason}
                  onChange={e => setCompensateForm({ ...compensateForm, reason: e.target.value })}
                  rows={3}
                  placeholder={isHalf ? "e.g., Left early for medical appointment, half-day compensation approved" : "e.g., Employee was ill with fever, medical certificate attached; compensating as sick leave"}
                  data-testid="compensate-reason"
                />
                <p className="text-[11px] text-slate-400 mt-1">This will deduct {needed} day from the chosen balance and adjust payroll on next run.</p>
              </div>
              <div className="flex gap-2 justify-end pt-2">
                <Button variant="outline" size="sm" onClick={() => setCompensateOpen(false)} data-testid="compensate-cancel">Cancel</Button>
                <Button size="sm" className="bg-[#0D2850] hover:bg-[#0B2246]" onClick={handleCompensateSave} disabled={compensateSaving || (compensateForm.leave_type === 'AUTO' ? ((compensateBalances.CL + compensateBalances.EL + compensateBalances.SL) < needed) : (compensateBalances[compensateForm.leave_type] < needed))} data-testid="compensate-save">
                  {compensateSaving ? 'Saving…' : (isHalf ? 'Compensate Half-Day' : 'Compensate Absence')}
                </Button>
              </div>
            </div>
          );})()}
        </DialogContent>
      </Dialog>


      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-md max-h-[90vh] flex flex-col p-0 gap-0" data-testid="attendance-edit-dialog">
          <DialogHeader className="px-6 pt-6 pb-2 shrink-0 border-b border-slate-100">
            <DialogTitle className="text-[#0B1B33]">{editPrefilled ? 'Edit Attendance' : (editForm.mode === 'bulk' ? 'Bulk Attendance Update' : 'Mark / Edit Attendance')}</DialogTitle>
            <p className="text-xs text-slate-400">
              {editForm.mode === 'bulk'
                ? 'Apply the same status to every day in the chosen range. Sundays / existing records can be skipped.'
                : (editPrefilled ? 'Updating an existing record.' : 'Choose an employee + date. If a record exists it will be loaded automatically.')}
            </p>
          </DialogHeader>
          <div className="space-y-3 px-6 py-4 overflow-y-auto flex-1">
            {/* Mode toggle */}
            <div className="flex items-center gap-1 p-1 bg-slate-100 rounded-lg" data-testid="mode-toggle">
              <button
                type="button"
                onClick={() => setEditForm(f => ({ ...f, mode: 'single' }))}
                className={`flex-1 text-xs font-medium py-1.5 rounded-md transition-colors ${editForm.mode === 'single' ? 'bg-white text-[#0D2850] shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                data-testid="mode-single"
              >
                <UserCheck className="w-3.5 h-3.5 inline mr-1" /> Single Day
              </button>
              <button
                type="button"
                onClick={() => { setEditForm(f => ({ ...f, mode: 'bulk' })); setEditPrefilled(false); setEditingRecordId(null); }}
                className={`flex-1 text-xs font-medium py-1.5 rounded-md transition-colors ${editForm.mode === 'bulk' ? 'bg-white text-[#0D2850] shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                data-testid="mode-bulk"
              >
                <CalendarRange className="w-3.5 h-3.5 inline mr-1" /> Date Range (Bulk)
              </button>
            </div>

            <div>
              <Label className="text-xs">Employee <span className="text-red-500">*</span></Label>
              <Select
                value={editForm.employee_id}
                onValueChange={v => { setEditForm(f => ({ ...f, employee_id: v })); setEditPrefilled(false); setEditingRecordId(null); }}
                disabled={editPrefilled && !!editingRecordId}
              >
                <SelectTrigger data-testid="edit-att-employee"><SelectValue placeholder="Select employee" /></SelectTrigger>
                <SelectContent>
                  {employees.map(e => <SelectItem key={e.id} value={e.id}>{e.name} ({e.employee_code})</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            {editForm.mode === 'single' ? (
              <div className="flex gap-2">
                <div className="flex-1">
                  <Label className="text-xs">Date <span className="text-red-500">*</span></Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className="w-full justify-start font-normal h-9 px-3 text-sm"
                        disabled={editPrefilled && !!editingRecordId}
                        data-testid="edit-att-date"
                      >
                        <CalendarDays className="w-3.5 h-3.5 mr-2 text-slate-400" />
                        {editForm.date ? format(new Date(editForm.date), 'dd MMM yyyy') : 'Pick a date'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={editForm.date ? new Date(editForm.date) : undefined}
                        onSelect={(d) => {
                          if (!d) return;
                          setEditForm(f => ({ ...f, date: format(d, 'yyyy-MM-dd') }));
                          setEditPrefilled(false);
                          setEditingRecordId(null);
                        }}
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                {!editPrefilled && (
                  <div className="flex items-end">
                    <Button
                      type="button" variant="outline" size="sm" className="h-9"
                      onClick={lookupAttendance}
                      disabled={!editForm.employee_id || !editForm.date}
                      data-testid="edit-att-lookup"
                    >
                      Lookup
                    </Button>
                  </div>
                )}
              </div>
            ) : (
              <div className="space-y-2">
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs">From Date <span className="text-red-500">*</span></Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="w-full justify-start font-normal h-9 px-3 text-sm" data-testid="edit-att-from-date">
                          <CalendarDays className="w-3.5 h-3.5 mr-2 text-slate-400" />
                          {editForm.from_date ? format(new Date(editForm.from_date), 'dd MMM yyyy') : 'Pick'}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar mode="single"
                          selected={editForm.from_date ? new Date(editForm.from_date) : undefined}
                          onSelect={d => d && setEditForm(f => ({ ...f, from_date: format(d, 'yyyy-MM-dd') }))}
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                  <div>
                    <Label className="text-xs">To Date <span className="text-red-500">*</span></Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="w-full justify-start font-normal h-9 px-3 text-sm" data-testid="edit-att-to-date">
                          <CalendarDays className="w-3.5 h-3.5 mr-2 text-slate-400" />
                          {editForm.to_date ? format(new Date(editForm.to_date), 'dd MMM yyyy') : 'Pick'}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar mode="single"
                          selected={editForm.to_date ? new Date(editForm.to_date) : undefined}
                          onSelect={d => d && setEditForm(f => ({ ...f, to_date: format(d, 'yyyy-MM-dd') }))}
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                </div>
                <div className="flex flex-col gap-1.5 bg-slate-50 rounded-lg p-2.5">
                  <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer">
                    <input type="checkbox" checked={editForm.skip_sundays} onChange={e => setEditForm(f => ({ ...f, skip_sundays: e.target.checked }))} data-testid="edit-att-skip-sun" />
                    Skip Sundays
                  </label>
                  <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer">
                    <input type="checkbox" checked={editForm.skip_existing} onChange={e => setEditForm(f => ({ ...f, skip_existing: e.target.checked }))} data-testid="edit-att-skip-existing" />
                    Skip days that already have a record (don&apos;t overwrite real clock-ins)
                  </label>
                </div>
              </div>
            )}
            <div>
              <Label className="text-xs">Status <span className="text-red-500">*</span></Label>
              <Select value={editForm.status} onValueChange={v => setEditForm(f => ({ ...f, status: v }))}>
                <SelectTrigger data-testid="edit-att-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="present">Present</SelectItem>
                  <SelectItem value="late">Late</SelectItem>
                  <SelectItem value="half_day">Half Day</SelectItem>
                  <SelectItem value="absent">Absent</SelectItem>
                  <SelectItem value="on_leave">On Leave</SelectItem>
                  <SelectItem value="holiday">Holiday</SelectItem>
                  <SelectItem value="wfh">Work From Home</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">Clock-In Time</Label>
                <Input type="time" value={editForm.clock_in_time?.slice(0, 5) || ''} onChange={e => setEditForm(f => ({ ...f, clock_in_time: e.target.value }))} data-testid="edit-att-clockin" />
              </div>
              <div>
                <Label className="text-xs">Clock-Out Time</Label>
                <Input type="time" value={editForm.clock_out_time?.slice(0, 5) || ''} onChange={e => setEditForm(f => ({ ...f, clock_out_time: e.target.value }))} data-testid="edit-att-clockout" />
              </div>
            </div>
            <div>
              <Label className="text-xs">Remarks (optional)</Label>
              <Input value={editForm.remarks} onChange={e => setEditForm(f => ({ ...f, remarks: e.target.value }))} placeholder="Any note about this entry" data-testid="edit-att-remarks" />
            </div>
            <div>
              <Label className="text-xs">Reason for edit <span className="text-red-500">*</span></Label>
              <Textarea
                rows={2} value={editForm.reason_for_edit}
                onChange={e => setEditForm(f => ({ ...f, reason_for_edit: e.target.value }))}
                placeholder="e.g. Forgot to clock-in, Manager confirmed presence"
                data-testid="edit-att-reason"
              />
              <p className="text-[10px] text-slate-400 mt-1">Logged in audit trail with your name & timestamp.</p>
            </div>
          </div>
          <div className="px-6 py-3 border-t border-slate-100 shrink-0 flex gap-2 bg-white">
            <Button variant="outline" className="flex-1" onClick={() => setEditOpen(false)}>Cancel</Button>
            <Button className="flex-1 bg-[#0D2850] hover:bg-[#0B2246]" onClick={handleSaveEdit} disabled={editSaving} data-testid="edit-att-save">
              {editSaving ? 'Saving...' : (
                editForm.mode === 'bulk'
                  ? `Apply to ${(() => {
                      try {
                        const a = new Date(editForm.from_date), b = new Date(editForm.to_date);
                        return Math.max(0, Math.round((b - a) / 86400000) + 1);
                      } catch { return '?'; }
                    })()} day(s)`
                  : (editPrefilled && editingRecordId ? 'Update' : 'Save Attendance')
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}


function MonthlyFilters({ month, year, setMonth, setYear, search, setSearch, deptFilter, setDeptFilter, empFilter, setEmpFilter, departments, employees, onRefresh, fullAccess }) {
  return (
    <div className="flex gap-2 items-center flex-wrap">
      <Select value={String(month)} onValueChange={v => setMonth(Number(v))}>
        <SelectTrigger className="w-[130px] h-9"><SelectValue /></SelectTrigger>
        <SelectContent>{MONTHS.map((m, i) => <SelectItem key={i} value={String(i + 1)}>{m}</SelectItem>)}</SelectContent>
      </Select>
      <Select value={String(year)} onValueChange={v => setYear(Number(v))}>
        <SelectTrigger className="w-[90px] h-9"><SelectValue /></SelectTrigger>
        <SelectContent>{[2025, 2026, 2027].map(y => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
      </Select>
      {fullAccess && departments && (
        <Select value={deptFilter} onValueChange={setDeptFilter}>
          <SelectTrigger className="w-[140px] h-9" data-testid="dept-filter"><SelectValue placeholder="Department" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="All">All Depts</SelectItem>
            {departments.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
          </SelectContent>
        </Select>
      )}
      {fullAccess && employees && (
        <Select value={empFilter} onValueChange={setEmpFilter}>
          <SelectTrigger className="w-[170px] h-9" data-testid="emp-filter"><SelectValue placeholder="Employee" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="All">All Employees</SelectItem>
            {employees.map(e => <SelectItem key={e.id} value={e.id}>{e.name} ({e.employee_code})</SelectItem>)}
          </SelectContent>
        </Select>
      )}
      {fullAccess && (
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
          <Input placeholder="Search name..." value={search} onChange={e => setSearch(e.target.value)} className="pl-8 h-9 w-[150px]" data-testid="attendance-search" />
        </div>
      )}
      <Button variant="outline" size="sm" onClick={onRefresh}><RefreshCw className="w-4 h-4 mr-1" />Refresh</Button>
    </div>
  );
}


function RecordsTable({ records, loading, viewDetail, fullAccess, statusBadge, onEdit, onCompensate }) {
  const statusColor = (s) => ({
    present: 'bg-emerald-100 text-emerald-700',
    absent: 'bg-red-100 text-red-700',
    late: 'bg-amber-100 text-amber-700',
    half_day: 'bg-orange-100 text-orange-700',
    on_leave: 'bg-blue-100 text-blue-700',
    holiday: 'bg-violet-100 text-violet-700',
    wfh: 'bg-teal-100 text-teal-700',
    weekly_off: 'bg-slate-100 text-slate-500',
  }[s] || 'bg-slate-100 text-slate-600');

  return (
    <Card className="border-slate-200">
      <CardContent className="p-0">
        {loading ? (
          <div className="p-6 space-y-4">{[1,2,3,4,5].map(i => <Skeleton key={i} className="h-10" />)}</div>
        ) : records.length === 0 ? (
          <div className="p-12 text-center">
            <CalendarDays className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="text-sm text-slate-400">No attendance data for this period</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead>Employee</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Clock In</TableHead>
                  <TableHead className="hidden sm:table-cell">Clock Out</TableHead>
                  {fullAccess && <TableHead className="hidden md:table-cell">GPS</TableHead>}
                  {fullAccess && <TableHead className="w-[60px] hidden sm:table-cell">Proof</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {records.slice(0, 200).map(r => (
                  <TableRow key={r.id} className={r.is_synthetic ? (r.status === 'weekly_off' ? 'bg-slate-50/40' : r.status === 'holiday' ? 'bg-violet-50/40' : 'bg-red-50/40') : (r.is_late ? 'bg-amber-50/30' : '')}>
                    <TableCell>
                      <p className="text-sm font-medium">{r.employee_name}</p>
                      <p className="text-xs text-slate-400">{r.employee_code}</p>
                    </TableCell>
                    <TableCell className="text-sm">{r.date}</TableCell>
                    <TableCell>
                      {(r.status === 'on_leave' && (r.leave_type || '').toUpperCase() === 'MLP') ? (
                        <Badge className="bg-pink-100 text-pink-700 hover:bg-pink-100 text-xs" data-testid={`row-mlp-badge-${r.id}`}>MLP</Badge>
                      ) : (r.status === 'on_leave' && (r.leave_type || '').toUpperCase() === 'COMP_OFF') ? (
                        <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100 text-xs" data-testid={`row-compoff-badge-${r.id}`}>Comp-Off</Badge>
                      ) : (
                        <Badge className={`${statusColor(r.status)} hover:${statusColor(r.status)} text-xs`}>{r.status}</Badge>
                      )}
                      {r.half_day_deducted && <Badge className="ml-1 bg-red-100 text-red-600 text-[10px]">-0.5</Badge>}
                    </TableCell>
                    <TableCell className="text-sm">{r.clock_in_time || (r.is_synthetic ? <span className="text-slate-300">—</span> : '-')}</TableCell>
                    <TableCell className="text-sm hidden sm:table-cell">{r.clock_out_time || '-'}</TableCell>
                    {fullAccess && (
                      <TableCell className="hidden md:table-cell">
                        {r.clock_in_gps ? (
                          <a href={`https://www.google.com/maps?q=${r.clock_in_gps.lat},${r.clock_in_gps.lng}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs text-blue-600 hover:underline"><MapPin className="w-3 h-3" />Map</a>
                        ) : <span className="text-xs text-slate-400">-</span>}
                      </TableCell>
                    )}
                    {fullAccess && (
                      <TableCell className="hidden sm:table-cell">
                        <div className="flex items-center gap-0.5">
                          {!r.is_synthetic && <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => viewDetail(r.id)}><Eye className="w-4 h-4 text-slate-500" /></Button>}
                          {onEdit && (
                            <Button variant="ghost" size="sm" className="h-7 w-7 p-0" title={r.is_synthetic ? 'Mark attendance' : 'Edit attendance'} onClick={() => onEdit(r)} data-testid={`edit-attendance-row-${r.id}`}><Pencil className="w-4 h-4 text-[#0D2850]" /></Button>
                          )}
                          {(r.status === 'absent' || r.status === 'half_day' || r.half_day_deducted) && onCompensate && (
                            <Button variant="ghost" size="sm" className="h-7 w-7 p-0" title={r.status === 'absent' ? 'Compensate absence with EL/CL/SL' : 'Compensate half-day with 0.5 EL/CL/SL'} onClick={() => onCompensate(r)} data-testid={`compensate-row-${r.id}`}><HeartHandshake className="w-4 h-4 text-emerald-600" /></Button>
                          )}
                        </div>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
